import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Row, Col, Button, Form, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ColumnSchema } from '../../types/customReport';
import DraggableColumn from './DraggableColumn';
import { Add as AddIcon, Check as CheckIcon, Search as SearchIcon } from '@mui/icons-material';

interface Props {
  allColumns: ColumnSchema[];
  selectedColumns: ColumnSchema[];
  onSelectedChange: (columns: ColumnSchema[]) => void;
}

export const ColumnSelector: React.FC<Props> = ({ allColumns, selectedColumns, onSelectedChange }) => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAdd = (col: ColumnSchema) => {
    onSelectedChange([...selectedColumns, col]);
  };

  const handleRemove = (key: string) => {
    onSelectedChange(selectedColumns.filter((c) => c.key !== key));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedColumns.findIndex((c) => c.key === active.id);
      const newIndex = selectedColumns.findIndex((c) => c.key === over.id);
      onSelectedChange(arrayMove(selectedColumns, oldIndex, newIndex));
    }
  };

  const handleSelectDefaults = () => {
    const defaults = allColumns.filter((c) => c.default);
    onSelectedChange(defaults);
  };

  return (
    <Row>
      <Col md={5}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">{t('customReports.availableColumns')}</h6>
          <Button variant="link" size="sm" onClick={handleSelectDefaults} className="p-0">
            {t('customReports.selectAllDefaults')}
          </Button>
        </div>
        <div className="position-relative mb-2">
          <Form.Control
            size="sm"
            placeholder={t('buttons.search', { ns: 'common' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-4"
          />
          <SearchIcon 
            style={{ 
              position: 'absolute', 
              insetInlineStart: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              fontSize: '16px',
              color: theme.palette.text.secondary
            }} 
          />
        </div>
        <div style={{ maxHeight: '400px', overflowY: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: '10px', padding: '8px' }}>
          {allColumns.map((col) => {
            const isSelected = selectedColumns.some((s) => s.key === col.key);
            if (searchTerm && !col.label.toLowerCase().includes(searchTerm.toLowerCase())) return null;
            
            return (
              <div 
                key={col.key} 
                className={`d-flex justify-content-between align-items-center p-2 mb-1 border rounded ${isSelected ? 'bg-light text-muted' : ''}`}
                style={{ fontSize: '0.9rem' }}
              >
                <span>
                  {col.label}
                  <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>{t(`customReports.columnTypes.${col.type}`)}</Badge>
                </span>
                {isSelected ? (
                  <CheckIcon style={{ fontSize: '18px', color: theme.palette.success.main }} />
                ) : (
                  <Button variant="outline-primary" size="sm" onClick={() => handleAdd(col)} className="p-0" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AddIcon style={{ fontSize: '16px' }} />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Col>

      <Col md={7}>
        <h6 className="mb-2">{t('customReports.selectedColumns')}</h6>
        <div style={{ minHeight: '400px', border: `1px solid ${theme.palette.divider}`, borderRadius: '10px', padding: '8px' }}>
          {selectedColumns.length === 0 ? (
            <div className="text-center text-muted mt-5">
              {t('customReports.minOneColumn')}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedColumns.map((c) => c.key)}
                strategy={verticalListSortingStrategy}
              >
                {selectedColumns.map((col) => (
                  <DraggableColumn
                    key={col.key}
                    column={col}
                    onRemove={() => handleRemove(col.key)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
        <small className="text-muted mt-2 d-block">
          {t('customReports.dragToReorder')}
        </small>
      </Col>
    </Row>
  );
};
